// ============================================
// src/components/maintenance/MaintenanceTaskForm.js
// FIXED VERSION - Complete Form Display
// ============================================

import React, { useState, useEffect } from 'react';

function MaintenanceTaskForm({ 
  task, 
  categories, 
  vendors, 
  templates,
  onSave, 
  onClose 
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    task_type: 'one_time',
    priority: 'medium',
    status: 'scheduled',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: '',
    duration_hours: 1,
    assigned_vendor_id: '',
    estimated_cost: '',
    notes: '',
    is_amc: false,
    amc_document_url: '',
    amc_end_date: '',
    recurrence_pattern: {
      type: 'monthly',
      interval: 1,
      dayOfMonth: new Date().getDate(),
      dayOfWeek: 1,
      endDate: ''
    },
    enable_reminder: true
  });

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showRecurrenceOptions, setShowRecurrenceOptions] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        ...task,
        scheduled_date: task.scheduled_date || new Date().toISOString().split('T')[0],
        recurrence_pattern: task.recurrence_pattern || formData.recurrence_pattern
      });
      setShowRecurrenceOptions(task.task_type === 'recurring');
    }
  }, [task]);

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        title: template.name,
        description: template.description,
        category_id: template.category_id,
        duration_hours: template.default_duration_hours,
        estimated_cost: template.default_estimated_cost,
        assigned_vendor_id: template.default_vendor_id || '',
        task_type: template.default_recurrence ? 'recurring' : 'one_time',
        recurrence_pattern: template.default_recurrence || prev.recurrence_pattern
      }));
      
      if (template.default_recurrence) {
        setShowRecurrenceOptions(true);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category_id || !formData.scheduled_date) {
      alert('Please fill in all required fields (Title, Category, and Date)');
      return;
    }

    const dataToSave = {
      ...formData,
      estimated_cost: parseFloat(formData.estimated_cost) || 0,
      duration_hours: parseInt(formData.duration_hours) || 1,
      recurrence_pattern: formData.task_type === 'recurring' ? formData.recurrence_pattern : null,
      scheduled_time: formData.scheduled_time || null,
      assigned_vendor_id: formData.assigned_vendor_id || null,
      description: formData.description || null,
      notes: formData.notes || null,
      amc_document_url: formData.amc_document_url || null,
      amc_end_date: formData.amc_end_date || null
    };

    onSave(dataToSave);
  };

  const recurrenceTypes = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: '#28a745' },
    { value: 'medium', label: 'Medium', color: '#ffc107' },
    { value: 'high', label: 'High', color: '#fd7e14' },
    { value: 'urgent', label: 'Urgent', color: '#dc3545' }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        {/* Fixed Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          background: 'white',
          borderRadius: '10px 10px 0 0',
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              {task ? 'Edit Task' : 'Add Maintenance Task'}
            </h3>
            <button
              onClick={onClose}
              type="button"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          paddingBottom: '0'
        }}>
          <form onSubmit={handleSubmit} id="maintenance-form">
            {/* Title - REQUIRED - FIRST FIELD */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Title <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                style={inputStyle}
                placeholder="e.g., Monthly Lift Maintenance"
                required
                autoFocus
              />
            </div>

            {/* Category - REQUIRED - SECOND FIELD */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Category <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                style={inputStyle}
                required
              >
                <option value="">-- Select Category --</option>
                {categories && categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Task Type - THIRD FIELD */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Task Type</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({...formData, task_type: 'one_time'});
                    setShowRecurrenceOptions(false);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: formData.task_type === 'one_time' ? '#667eea' : '#f0f0f0',
                    color: formData.task_type === 'one_time' ? 'white' : '#666',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  One Time
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({...formData, task_type: 'recurring'});
                    setShowRecurrenceOptions(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: formData.task_type === 'recurring' ? '#667eea' : '#f0f0f0',
                    color: formData.task_type === 'recurring' ? 'white' : '#666',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🔄 Recurring
                </button>
              </div>
            </div>

            {/* Template Selection - Optional */}
            {!task && templates && templates.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Use Template (Optional)
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    if (e.target.value) handleTemplateSelect(e.target.value);
                  }}
                  style={inputStyle}
                >
                  <option value="">-- Select Template --</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Priority - MOVED AFTER REQUIRED FIELDS */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Priority</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {priorityOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({...formData, priority: option.value})}
                    style={{
                      padding: '10px',
                      background: formData.priority === option.value ? option.color : '#f0f0f0',
                      color: formData.priority === option.value ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                style={{...inputStyle, minHeight: '80px', resize: 'vertical'}}
                placeholder="Add task details..."
              />
            </div>

            {/* Date and Time */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '10px',
              marginBottom: '20px'
            }}>
              <div>
                <label style={labelStyle}>
                  Date <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Time</label>
                <input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Recurrence Options */}
            {showRecurrenceOptions && (
              <div style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                  Recurrence Settings
                </h4>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={labelStyle}>Repeat Every</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="number"
                      min="1"
                      value={formData.recurrence_pattern.interval}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurrence_pattern: {
                          ...formData.recurrence_pattern,
                          interval: parseInt(e.target.value) || 1
                        }
                      })}
                      style={{...inputStyle, flex: '0 0 80px'}}
                    />
                    <select
                      value={formData.recurrence_pattern.type}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurrence_pattern: {
                          ...formData.recurrence_pattern,
                          type: e.target.value
                        }
                      })}
                      style={{...inputStyle, flex: 1}}
                    >
                      {recurrenceTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.recurrence_pattern.type === 'monthly' && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={labelStyle}>Day of Month</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.recurrence_pattern.dayOfMonth}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurrence_pattern: {
                          ...formData.recurrence_pattern,
                          dayOfMonth: parseInt(e.target.value) || 1
                        }
                      })}
                      style={inputStyle}
                    />
                  </div>
                )}

                <div>
                  <label style={labelStyle}>End Date (Optional)</label>
                  <input
                    type="date"
                    value={formData.recurrence_pattern.endDate}
                    onChange={(e) => setFormData({
                      ...formData,
                      recurrence_pattern: {
                        ...formData.recurrence_pattern,
                        endDate: e.target.value
                      }
                    })}
                    style={inputStyle}
                  />
                </div>
              </div>
            )}

            {/* Vendor Assignment */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Assign to Vendor</label>
              <select
                value={formData.assigned_vendor_id}
                onChange={(e) => setFormData({...formData, assigned_vendor_id: e.target.value})}
                style={inputStyle}
              >
                <option value="">-- Select Vendor --</option>
                {vendors && vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name} {vendor.phone ? `(${vendor.phone})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Cost and Duration */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '10px',
              marginBottom: '20px'
            }}>
              <div>
                <label style={labelStyle}>Estimated Cost (₹)</label>
                <input
                  type="number"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                  style={inputStyle}
                  placeholder="0.00"
                  min="0"
                  step="100"
                />
              </div>
              <div>
                <label style={labelStyle}>Duration (Hours)</label>
                <input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
                  style={inputStyle}
                  min="1"
                  step="1"
                />
              </div>
            </div>

            {/* AMC Details */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_amc}
                  onChange={(e) => setFormData({...formData, is_amc: e.target.checked})}
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                  This is an AMC task
                </span>
              </label>
            </div>

            {formData.is_amc && (
              <div style={{
                background: '#e3f2fd',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={labelStyle}>AMC End Date</label>
                  <input
                    type="date"
                    value={formData.amc_end_date}
                    onChange={(e) => setFormData({...formData, amc_end_date: e.target.value})}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>AMC Document URL</label>
                  <input
                    type="url"
                    value={formData.amc_document_url}
                    onChange={(e) => setFormData({...formData, amc_document_url: e.target.value})}
                    style={inputStyle}
                    placeholder="https://..."
                  />
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Additional Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                style={{...inputStyle, minHeight: '60px', resize: 'vertical'}}
                placeholder="Any special instructions..."
              />
            </div>

            {/* Reminder */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.enable_reminder}
                  onChange={(e) => setFormData({...formData, enable_reminder: e.target.checked})}
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                  Send reminder 1 day before
                </span>
              </label>
            </div>
          </form>
        </div>

        {/* Fixed Footer with Buttons */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e0e0e0',
          background: 'white',
          borderRadius: '0 0 10px 10px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              form="maintenance-form"
              style={{
              flex: '1 1 0',     // identical on both buttons
              minWidth: 0,       // lets both shrink equally
              padding: '15px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px'
              }}
            >
              {task ? 'UPDATE TASK' : 'CREATE TASK'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
              flex: '1 1 0',     // identical on both buttons
              minWidth: 0,       // prevents content from forcing a wider min-width
              padding: '15px',
              background: '#6c757d',
                color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: '600',
  fontSize: '14px',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.3s',
  boxSizing: 'border-box'
};

export default MaintenanceTaskForm;